import {
	type InputRule,
	ellipsis,
	emDash,
	inputRules,
	smartQuotes,
} from "@handlewithcare/prosemirror-inputrules";
import { type MarkType, type NodeType, type Schema } from "prosemirror-model";

import { blockQuoteRule } from "./blockQuoteRule";
import { codeBlockRule } from "./codeBlockRule";
import { emUnderscoreRule } from "./emUnderscoreRule";
import { headingRule } from "./headingRule";
import { horizontalRuleRule } from "./horizontalRuleRule";
import { inlineCodeRule } from "./inlineCodeRule";
import { kbdRule } from "./kbdRule";
import { linkCodeSnippetRule } from "./linkCodeSnippetRule";
import { linkPullRequestRule } from "./linkPullRequestRule";
import { linkRuleMd } from "./linkRuleMd";
import { linkRuleUrl } from "./linkRuleUrl";
import { listItemRules } from "./listItemRule";
import { pageLinkRule } from "./pageLinkRule";
import { specialCharacters } from "./specialCharacters";
import { strikeTextRule } from "./strikeTextRule";
import { strongAsterixRule } from "./strongAsterixRule";
import { toDoRules } from "./toDoRule";

// A set of input rules for creating the basic block quotes, lists,
// code blocks, and heading.
export const buildInputRules = (schema: Schema) => {
	const rules = smartQuotes.concat(ellipsis, emDash);

	function addIfPresent<T extends MarkType | NodeType>(
		t: T | undefined,
		f: (t: T) => InputRule | InputRule[]
	) {
		if (t) {
			const r = f(t);
			const rs = Array.isArray(r) ? r : [r];
			rules.push(...rs);
		}
	}

	// Nodes
	addIfPresent(schema.nodes["horizontal_rule"], horizontalRuleRule);
	addIfPresent(schema.nodes["blockquote"], blockQuoteRule);
	addIfPresent(schema.nodes["list_item"], listItemRules);
	addIfPresent(schema.nodes["to_do_item"], toDoRules);
	addIfPresent(schema.nodes["code_block"], codeBlockRule);
	addIfPresent(schema.nodes["heading"], (t: NodeType) => headingRule(t, 6));
	addIfPresent(schema.nodes["pull_request"], linkPullRequestRule);
	addIfPresent(schema.nodes["code_snippet"], linkCodeSnippetRule);
	addIfPresent(schema.nodes["page_link"], pageLinkRule);

	// Marks
	addIfPresent(schema.marks["link"], linkRuleMd);
	addIfPresent(schema.marks["link"], linkRuleUrl);

	addIfPresent(schema.marks["strong"], strongAsterixRule);
	addIfPresent(schema.marks["em"], emUnderscoreRule);
	addIfPresent(schema.marks["strike"], strikeTextRule);
	addIfPresent(schema.marks["kbd"], kbdRule);
	addIfPresent(schema.marks["code"], inlineCodeRule);

	// Character Replacement
	rules.push(...specialCharacters);

	return inputRules({ rules });
};
